<template>
  <!-- Datenmanagement im gemeinsamen App-Rahmen (Seitenleiste, Benutzermenü, Logout). -->
  <AppShell>
    <AppPage
      :crumbs="[{ label: 'Datenmanagement' }]"
      title="Datenmanagement"
      icon="fa-database"
      icon-color="#6B6E76"
    >
    <div class="dm-content">
      <!-- Erste Reihe: AKD-Datei und Plan-Datei -->
      <div class="row g-4 align-items-stretch">
        <!-- AKD-Karte -->
        <div class="col-12 col-md-6 mb-3 d-flex">
          <div class="flip-card flex-fill h-100">
            <div class="flip-card-inner" :class="{ 'is-flipped': isFlipped.akd }">
              <!-- Vorderseite -->
              <div class="flip-card-front h-100">
                <div class="card drop-area h-100 position-relative" @drop.prevent="handleAKDDrop" @dragover.prevent>
                  <div class="card-body text-center">
                    <p>Ziehe die <strong>AKD-Datei</strong> hierher oder klicke, um sie auszuwählen.</p>
                    <input type="file" ref="akd_fileInput" style="display:none" @change="handleAKDFileSelect" />
                    <button class="btn btn-primary" @click="selectAKDFile" :disabled="loading">
                      AKD-Datei auswählen
                    </button>
                    <br><br>
                    <span>
                      <strong>Dateiname:</strong> {{ akd_file_name || '–' }}<br>
                      <strong>Hochgeladen am:</strong> {{ aktu_akd_date || '–' }} Uhr<br>
                      <strong>Hochgeladen von:</strong> {{ updated_by_akd || '–' }}
                    </span>
                    <button class="info-button" @click.stop="flipCard('akd')">
                      <i class="fas fa-info-circle"></i>
                    </button>
                    <a target="_blank"
                      v-if="akd_file_name"
                      :href="getDownloadUrl(akd_file_name)"
                      download
                      class="download-button"
                      :title="'AKD-Datei herunterladen'"
                    >
                      <i class="fas fa-download"></i>
                    </a>
                  </div>
                </div>
              </div>
              <!-- Rückseite -->
              <div class="flip-card-back h-100">
                <div class="card h-100">
                  <div class="card-body text-center">
                    <h5>AKD-Datei Information</h5>
                    <p>
                      <strong>Excel-Struktur</strong><br>
                      Registerkarte muss den Namen "Datengrundlage" + Jahr haben (z.B. "Datengrundlage2025", "Datengrundlage2026")<br>
                      <strong>Projekt-Matching</strong><br>
                      Die Namen der Projekte, die in die Cockpit-Datenbank importiert werden sollen,
                      müssen in der Projekt-Matching-Tabelle existieren.
                    </p>
                    <button class="info-button" @click="flipCard('akd')">
                      <i class="fa-solid fa-circle-arrow-left"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Plan-Karte -->
        <div class="col-12 col-md-6 mb-3 d-flex">
          <div class="flip-card flex-fill h-100">
            <div class="flip-card-inner" :class="{ 'is-flipped': isFlipped.plan }">
              <!-- Vorderseite -->
              <div class="flip-card-front h-100">
                <div class="card drop-area h-100 position-relative" @drop.prevent="handlePlanDrop" @dragover.prevent>
                  <div class="card-body text-center">
                    <p>Ziehe die <strong>Merch Plan-Datei</strong> hierher oder klicke, um sie auszuwählen.</p>
                    <input type="file" ref="plan_fileInput" style="display:none" @change="handlePlanFileSelect" />
                    <button class="btn btn-primary" @click="selectPlanFile" :disabled="loading">
                      Merch Plan-Datei auswählen
                    </button>
                    <br><br>
                    <span>
                      <strong>Dateiname:</strong> {{ plan_file_name || '–' }}<br>
                      <strong>Hochgeladen am:</strong> {{ aktu_plan_date || '–' }} Uhr<br>
                      <strong>Hochgeladen von:</strong> {{ updated_by_plan || '–' }}
                    </span>
                    <button class="info-button" @click.stop="flipCard('plan')">
                      <i class="fas fa-info-circle"></i>
                    </button>
                    <a
                      v-if="plan_file_name"
                      :href="getDownloadUrl(plan_file_name)"
                      download
                      class="download-button"
                      :title="'Plan-Datei herunterladen'"
                    >
                      <i class="fas fa-download"></i>
                    </a>
                  </div>
                </div>
              </div>
              <!-- Rückseite -->
              <div class="flip-card-back h-100">
                <div class="card h-100">
                  <div class="card-body text-center">
                    <h5>Merch Plan-Datei Information</h5>
                    <p>
                      <strong>Excel-Struktur</strong><br>
                      Registerkarte muss den Namen "Datengrundlage" + Jahr haben (z.B. "Datengrundlage2025", "Datengrundlage2026").<br>
                      <strong>Nur Merch PLAN-Zeilen werden importiert.</strong><br>
                      Die Datei aktualisiert ausschließlich die Plan-Werte bestehender Projekte.
                    </p>
                    <button class="info-button" @click="flipCard('plan')">
                      <i class="fa-solid fa-circle-arrow-left"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Zweite Reihe: Ergebnis-Datei und Clockodo -->
      <div class="row g-4 align-items-stretch">
        <!-- Ergebnis-Karte -->
        <div class="col-12 col-md-6 mb-3 d-flex">
          <div class="flip-card flex-fill h-100">
            <div class="flip-card-inner" :class="{ 'is-flipped': isFlipped.ergebnis }">
              <!-- Vorderseite -->
              <div class="flip-card-front h-100">
                <div class="card drop-area flex-fill h-100 position-relative" @drop.prevent="handleErgebnisDrop" @dragover.prevent>
                  <div class="card-body text-center">
                    <p>Ziehe die <strong>Digital Ergebnis-Datei</strong> hierher oder klicke, um sie auszuwählen.</p>
                    <input type="file" ref="ergebnis_fileInput" style="display:none" @change="handleErgebnisFileSelect" />
                    <button class="btn btn-primary" @click="selectErgebnisFile" :disabled="loading">
                      Digital Ergebnis-Datei auswählen
                    </button>
                    <br><br>
                    <span>
                      <strong>Dateiname:</strong> {{ ergebnis_file_name || '–' }}<br>
                      <strong>Hochgeladen am:</strong> {{ aktu_ergebnis_date || '–' }} Uhr<br>
                      <strong>Hochgeladen von:</strong> {{ updated_by_ergebnis || '–' }}
                      <a
                        v-if="ergebnis_file_name"
                        :href="getDownloadUrl(ergebnis_file_name)"
                        class="download-button"
                        :title="'Ergebnis-Datei herunterladen'"
                      >
                        <i class="fas fa-download"></i>
                      </a>
                    </span>
                    <button class="info-button" @click.stop="flipCard('ergebnis')">
                      <i class="fas fa-info-circle"></i>
                    </button>
                  </div>
                </div>
              </div>
              <!-- Rückseite -->
              <div class="flip-card-back h-100">
                <div class="card h-100">
                  <div class="card-body text-center">
                    <h5>Digital Ergebnis-Datei Information</h5>
                    <p>
                      Diese Datei wird verwendet, um die <strong>Plan- und Ist-Daten der Digital-Projekte</strong> zu importieren.<br><br>
                      <strong>Hinweise:</strong><br>
                      - Die Datei muss die aktuellen Werte für Erlöse und Kosten der Digital-Projekte enthalten.<br>
                      - Die Daten werden nach dem Upload in die Cockpit-Datenbank übernommen und stehen für die Auswertung zur Verfügung.<br>
                      - Die Struktur der Datei sollte mit der Vorgabe abgestimmt sein.<br>
                      - Bereits vorhandene Digital-Ergebnisdaten werden beim Import überschrieben.
                    </p>
                    <button class="info-button" @click="flipCard('ergebnis')">
                      <i class="fa-solid fa-circle-arrow-left"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Clockodo-Karte -->
        <div class="col-12 col-md-6 mb-3 d-flex">
          <div class="flip-card flex-fill h-100">
            <div class="flip-card-inner" :class="{ 'is-flipped': isFlipped.clockodo }">
              <!-- Vorderseite -->
              <div class="flip-card-front h-100">
                <div class="card drop-area h-100 position-relative">
                  <div class="card-body text-center">
                    <p>Mit dieser Button kannst du die neuesten Daten aus <strong>ClockOdo</strong> importieren.</p>
                    <button class="btn btn-success" @click="fetchClockodoData" :disabled="loading">
                      Clockodo-Daten aktualisieren
                    </button>
                    <br><br>
                    <strong>Aktualisiert am:</strong> {{ aktu_clockodo_date || '–' }} Uhr<br>
                    <strong>Aktualisiert von:</strong> {{ updated_by_clockodo || '–' }}
                    <button class="info-button" @click.stop="flipCard('clockodo')">
                      <i class="fas fa-info-circle"></i>
                    </button>
                  </div>
                </div>
              </div>
              <!-- Rückseite -->
              <div class="flip-card-back h-100">
                <div class="card h-100">
                  <div class="card-body text-center">
                    <h5>Clockodo-Daten aktualisieren</h5>
                    <p><br>
                      <strong>Verwendung</strong><br><br>
                      Diese Funktion importiert Daten aus ClockOdo und ermöglicht es,
                      die Personalkosten für jedes Projekt neu zu berechnen.
                    </p>
                    <button class="info-button" @click="flipCard('clockodo')">
                      <i class="fa-solid fa-circle-arrow-left"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Dritte Reihe: Projekt-Matching-Karte und Zugriffsrechte-Karte -->
      <div class="row g-4 mt-4 align-items-stretch">
        <!-- Projekt-Matching-Karte -->
        <div class="col-12 col-md-6 mb-3 d-flex">
          <div class="flip-card flex-fill h-100">
            <div class="flip-card-inner" :class="{ 'is-flipped': isFlipped.projectMatching }">
              <!-- Vorderseite -->
              <div class="flip-card-front h-100">
                <div class="card drop-area h-100 position-relative" @drop.prevent="handleProjectMatchingDrop" @dragover.prevent>
                  <div class="card-body text-center">
                    <p>Ziehe die <strong>Merch-Projekt-Matching-Datei</strong> hierher oder klicke, um sie auszuwählen.</p>
                    <input type="file" ref="project_matching_fileInput" style="display:none" @change="handleProjectMatchingFileSelect" />
                    <button class="btn btn-primary" @click="selectProjectMatchingFile" :disabled="loading">
                      Merch-Projekt-Matching-Datei auswählen
                    </button>
                    <br><br>
                    <span>
                      <strong>Dateiname:</strong> {{ project_matching_file_name || '–' }}<br>
                      <strong>Hochgeladen am:</strong> {{ aktu_project_matching_date || '–' }} Uhr<br>
                      <strong>Hochgeladen von:</strong> {{ updated_by_project_matching || '–' }}
                    </span>
                    <button class="info-button" @click.stop="flipCard('projectMatching')">
                      <i class="fas fa-info-circle"></i>
                    </button>
                    <a
                      v-if="project_matching_file_name"
                      :href="getDownloadUrl(project_matching_file_name)"
                      download
                      class="download-button"
                      :title="'Merch-Projekt-Matching-Datei herunterladen'"
                    >
                      <i class="fas fa-download"></i>
                    </a>
                  </div>
                </div>
              </div>
              <!-- Rückseite -->
              <div class="flip-card-back h-100">
                <div class="card h-100">
                  <div class="card-body text-center">
                    <h5>Projekt-Matching Information</h5>
                    <p><br>
                      <strong>Verwendung</strong><br>
                      Diese Datei wird benötigt, um die Projekte aus verschiedenen Systemen
                      einander zuzuordnen. Die Matching-Datei enthält die Verknüpfungen zwischen, AKD-Projektnamen und Clockodo-Projektnamen.
                    </p>
                    <button class="info-button" @click="flipCard('projectMatching')">
                      <i class="fa-solid fa-circle-arrow-left"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
         <!-- Digital-Projekt-Matching-Karte -->
      <div class="col-12 col-md-6 mb-3 d-flex">
        <div class="flip-card flex-fill h-100">
          <div class="flip-card-inner" :class="{ 'is-flipped': isFlipped.digitalProjectMatching }">
            <!-- Vorderseite -->
            <div class="flip-card-front h-100">
              <div class="card drop-area h-100 position-relative" @drop.prevent="handleDigitalProjectMatchingDrop" @dragover.prevent>
                <div class="card-body text-center">
                  <p>Ziehe die <strong>Digital-Projekt-Matching-Datei</strong> hierher oder klicke, um sie auszuwählen.</p>
                  <input type="file" ref="digital_project_matching_fileInput" style="display:none" @change="handleDigitalProjectMatchingFileSelect" />
                  <button class="btn btn-primary" @click="selectDigitalProjectMatchingFile" :disabled="loading">
                    Digital-Projekt-Matching-Datei auswählen
                  </button>
                  <br><br>
                  <span>
                    <strong>Dateiname:</strong> {{ digital_project_matching_file_name || '–' }}<br>
                    <strong>Hochgeladen am:</strong> {{ aktu_digital_project_matching_date || '–' }} Uhr<br>
                    <strong>Hochgeladen von:</strong> {{ updated_by_digital_project_matching || '–' }}
                  </span>
                  <button class="info-button" @click.stop="flipCard('digitalProjectMatching')">
                    <i class="fas fa-info-circle"></i>
                  </button>
                  <a
                    v-if="digital_project_matching_file_name"
                    :href="getDownloadUrl(digital_project_matching_file_name)"
                    download
                    class="download-button"
                    :title="'Digital-Projekt-Matching-Datei herunterladen'"
                  >
                    <i class="fas fa-download"></i>
                  </a>
                </div>
              </div>
            </div>
            <!-- Rückseite -->
            <div class="flip-card-back h-100">
              <div class="card h-100">
                <div class="card-body text-center">
                  <h5>Digital-Projekt-Matching Information</h5>
                  <p><br>
                    <strong>Verwendung</strong><br>
                    Diese Datei wird benötigt, um Digital-Projekte aus verschiedenen Systemen einander zuzuordnen.<br>
                    Die Matching-Datei enthält die Verknüpfungen zwischen Digital-Projektnamen und Clockodo-Projektnamen.
                  </p>
                  <button class="info-button" @click="flipCard('digitalProjectMatching')">
                    <i class="fa-solid fa-circle-arrow-left"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    
    <div class="row g-4 mt-4 align-items-stretch">
      <!-- Zugriffsrechte-Karte -->
        <div class="col-12 col-md-6 mb-3 d-flex">
          <div class="card drop-area flex-fill h-100">
            <div class="card-body text-center">
              <p>
                Mit dieser Button kannst du die <strong>Zugriffsrechte</strong> der Nutzer verwalten.
              </p>
              <button class="btn btn-secondary mt-3" @click="openUserAccessModal">
                Zugriffsrechte verwalten
              </button>
            </div>
          </div>
        </div>
      </div>
     
      </div>
</div> <!-- Ende .container -->
    <!-- Notification -->
    <div v-if="notification_message" class="notification-box">
      {{ notification_message }}
    </div>

    <!-- User Access Modal -->
    <div v-if="show_user_access_modal" class="modal-overlay" @click="closeUserAccessModal">
      <div class="modal-content" @click.stop>
        <h3>Zugriffsrechte der Nutzer bearbeiten</h3>
        <button class="modal-close-button" @click="closeUserAccessModal">
          <span class="modal-close-icon">×</span>
        </button>
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Benutzername</th>
              <th>Module</th>
              <th>Marketing-Produkte</th>
              <th>Projekt-Marken</th>
              <th>Projekt-Familien</th>
              <th>Benutzer-Rolle</th>
              <th>Aktion</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in user_access_list" :key="index">
              <td><input v-model="item.username" class="form-control" /></td>
              <td>
                <div v-for="module_option in access_module_options" :key="module_option.value">
                  <label>
                    <input type="checkbox" :value="module_option.value" v-model="item.access_modules" />
                    {{ module_option.label }}
                  </label>
                </div>
              </td>
              <td>
                <div v-for="product in marketing_products" :key="product.id">
                  <label>
                    <input type="checkbox" :value="product.id" v-model="item.marketing_product_ids" />
                    {{ product.code }}
                  </label>
                </div>
              </td>
              <td>
                <div v-for="type_option in project_typ_options" :key="type_option">
                  <label>
                    <input type="checkbox" :value="type_option" v-model="item.projekt_typ" />
                    {{ type_option }}
                  </label>
                </div>
              </td>
              <td>
                <div v-for="fam_option in project_fam_options" :key="fam_option">
                  <label>
                    <input type="checkbox" :value="fam_option" v-model="item.projekt_fam" />
                    {{ fam_option }}
                  </label>
                </div>
              </td>
              <td>
                <select v-model="item.user_role" class="form-control">
                  <option value="admin">admin</option>
                  <option value="manager">manager</option>
                  <option value="user">user</option>
                </select>
              </td>
              <td>
                <button class="btn btn-success btn-sm" @click="saveUserAccess(item)">Speichern</button>
                <br/><br/>
                <button class="btn btn-danger btn-sm" @click="deleteUserAccess(item, index)">Löschen</button>
              </td>
            </tr>
          </tbody>
        </table>
        <button class="btn btn-primary mb-3" @click="addNewUserAccessEntry">
          Neuen Eintrag hinzufügen
        </button>
      </div>
    </div>

    <!-- Loader Modal -->
    <div v-if="loading" class="loader-modal">
      <img src="@/assets/loader.gif" alt="Loading..." class="loader" />
      <span class="sr-only"><strong>Vorgang läuft...</strong></span>
    </div>
    </AppPage>
  </AppShell>
</template>


<script>
import axios from 'axios';
import { getApiBaseUrl } from '@/config';
import Swal from 'sweetalert2';
import AppShell from '@/components/shell/AppShell.vue';
import AppPage from '@/components/ui/AppPage.vue';

export default {
  name: 'DatamanagementArea',
  components: {
    AppShell,
    AppPage
  },
  data() {
    return {
      username: '',
      aktu_akd_date: '',
      aktu_ergebnis_date: '',
      aktu_clockodo_date: '',
      path_akd: '',
      path_ergebnis: '',
      path_project_matching: '',
      updated_by_clockodo: '',
      aktu_project_matching_date: '',
      akd_file: null,
      ergebnis_file: null,
      project_matching_file: null,
      loading: false,
      project_typ_options: ['HEALTH', 'BUSINESS', 'FOMO', 'DTEST', 'INTERNATIONAL', 'MOBILITY'],
      project_fam_options: ['MERCHANDISING', 'DIGITAL', 'PRINT'],
      access_module_options: [
        { value: 'financial', label: 'Finanzdaten' },
        { value: 'marketing', label: 'Marketing-Dashboards' }
      ],
      marketing_products: [],
      show_user_access_modal: false,
      user_access_list: [],
      notification_message: '',
      isFlipped: {
        akd: false,
        ergebnis: false,
        clockodo: false,
        projectMatching: false,
        digitalProjectMatching: false
      },
      akd_file_name: null,
      ergebnis_file_name: null,
      project_matching_file_name: null,
      updated_by_akd: '',
      updated_by_ergebnis: '',
      updated_by_project_matching: '',
      digital_project_matching_file: null,
      digital_project_matching_file_name: null,
      aktu_digital_project_matching_date: '',
      updated_by_digital_project_matching: '',
      user_role: '',
      access_modules: [],
    };
  },
  // Beispiel im Script:
  computed: {
  },
  mounted() {
      const apiUrl = getApiBaseUrl();
      axios.get(`${apiUrl}/current_user/`, { withCredentials: true })
        .then(response => {
          this.username = response.data.username;
          this.user_role = response.data.user_role || this.user_role;
          this.access_modules = Array.isArray(response.data.access_modules) ? response.data.access_modules : [];
          if (this.username && this.username !== 'No User Information' && this.username !== 'No User Info') {
              this.user = this.username;
          }
        })
        .catch(() => {
          this.username = 'No User Information';
        });
      this.fetchUpdateDates();
    },

  methods: {
    getDownloadUrl(fileName) {
      if (!fileName) return '';
      const apiUrl = getApiBaseUrl();
      return `${apiUrl}/download_file/${fileName}/`;
    },
    getCsrfToken() {
      // Get CSRF token from cookie
      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith('csrftoken='))
        ?.split('=')[1];
      return cookieValue;
    },
    openUserAccessModal() {
      this.show_user_access_modal = true;
      this.loadUserAccessData();
    },
    adjustTimeForGermany(dateString) {
      if (!dateString) {
        return '';
      }
      try {
        // Deutsches Datum parsen (DD.MM.YYYY HH:MM)
        const [datePart, timePart] = dateString.split(' ');
        const [day, month, year] = datePart.split('.');
        const [hours, minutes] = timePart.split(':');
        // Datum im ISO-Format erstellen
        const date = new Date(year, month - 1, day, hours, minutes);
        // Überprüfen ob das Datum gültig ist
        if (isNaN(date.getTime())) {
          return 'Kein Datum verfügbar';
        }
        // Zeitzone anpassen (UTC+2)
        date.setHours(date.getHours() + 2);
        // Formatierung
        const formattedDate = date.toLocaleString('de-DE', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
        return formattedDate;
      } catch (error) {

        return 'Fehler beim Datum';
      }
    },
    flipCard(cardType) {
      this.isFlipped[cardType] = !this.isFlipped[cardType];
    },
    prepareUserAccessData(item) {
      return {
        ...item,
        projekt_typ: JSON.stringify(item.projekt_typ || []),
        projekt_fam: JSON.stringify(item.projekt_fam || []),
        access_modules: item.access_modules || ['financial'],
        marketing_product_ids: item.marketing_product_ids || []
      };
    },
    asList(value) {
      if (Array.isArray(value)) {
        return value;
      }
      if (!value) {
        return [];
      }
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    },
    closeUserAccessModal() {
      this.show_user_access_modal = false;
    },
    loadUserAccessData() {
      const apiUrl = getApiBaseUrl();
      axios.get(`${apiUrl}/useraccessrights/`)
        .then(response => {
          this.user_access_list = response.data.map(item => ({
            ...item,
            projekt_typ: this.asList(item.projekt_typ),
            projekt_fam: this.asList(item.projekt_fam),
            access_modules: this.asList(item.access_modules).length
              ? this.asList(item.access_modules)
              : ['financial'],
            marketing_product_ids: this.asList(item.marketing_product_ids)
          }));
        })
        .catch(err => {
          this.showNotification('Fehler beim Laden der UserAccess-Rechte.', err);
        });
      axios.get(`${apiUrl}/mb/products/`)
        .then(response => {
          this.marketing_products = response.data.products || [];
        })
        .catch(() => {
          this.marketing_products = [];
        });
    },
    addNewUserAccessEntry() {
      this.user_access_list.push({
        id: null,
        username: '',
        projekt_typ: [],
        projekt_fam: [],
        access_modules: ['financial'],
        marketing_product_ids: [],
        user_role: ''
      });
    },
    saveUserAccess(item) {
      const preparedData = this.prepareUserAccessData(item);
      const apiUrl = getApiBaseUrl();

      // Get CSRF token and include it in headers
      const csrfToken = this.getCsrfToken();
      const config = {
        withCredentials: true
      };
      if (csrfToken) {
        config.headers = {
          'X-CSRFToken': csrfToken
        };
      }

      if (!preparedData.id) {
        // POST: Neuer Eintrag
        axios.post(`${apiUrl}/useraccessrights/`, preparedData, config)
          .then(() => {
            Swal.fire({
              icon: 'success',
              title: 'Erfolg!',
              text: 'Neuer Eintrag wurde erstellt.'
            });
            this.loadUserAccessData();
          })
          .catch((error) => {
            Swal.fire({
              icon: 'error',
              title: 'Fehler!',
              text: 'Fehler beim Erstellen des Eintrags.' + error.message,
            });
          });
      } else {
        // PUT: Bestehender Eintrag
        axios.put(`${apiUrl}/useraccessrights/${preparedData.id}/`, preparedData, config)
          .then(() => {
            Swal.fire({
              icon: 'success',
              title: 'Erfolg!',
              text: 'Daten erfolgreich gespeichert.'
            });
            this.loadUserAccessData();
          })
          .catch((error) => {
            Swal.fire({
              icon: 'error',
              title: 'Fehler!',
              text: 'Fehler beim Speichern der Daten.' + error.message,
            });
          });
      }
    },
    deleteUserAccess(item, index) {
      const apiUrl = getApiBaseUrl();
      if (!item.id) {
        // Falls noch kein Eintrag angelegt wurde (id === null), nur lokal entfernen
        this.user_access_list.splice(index, 1);
        this.showNotification('Neuer Eintrag wurde entfernt (nicht gespeichert).');
      } else {
        // Get CSRF token and include it in headers
        const csrfToken = this.getCsrfToken();
        const config = {
          withCredentials: true
        };
        if (csrfToken) {
          config.headers = {
            'X-CSRFToken': csrfToken
          };
        }

        // DELETE-Request
        axios.delete(`${apiUrl}/useraccessrights/${item.id}/`, config)
          .then(() => {
            Swal.fire({
              icon: 'success',
              title: 'Erfolg!',
              text: 'Eintrag erfolgreich gelöscht.'
            });
            this.fetchUpdateDates();
          })
          .catch(() => {
            Swal.fire({
              icon: 'error',
              title: 'Fehler!',
              text: 'Fehler beim Löschen des Eintrags.'
            });
          });
      }
    },
    showNotification(message) {
      this.notification_message = message;
      setTimeout(() => {
        this.notification_message = '';
      }, 3000);
    },
    selectAKDFile() {
      this.$refs.akd_fileInput.click();
    },
    handleAKDFileSelect(event) {
      this.akd_file = event.target.files[0];
      this.uploadAKDFile();
    },
    selectProjectMatchingFile() {
      this.$refs.project_matching_fileInput.click();
    },
    handleProjectMatchingFileSelect(event) {
      this.project_matching_file = event.target.files[0];
      this.uploadProjectMatchingFile();
    },
    handleProjectMatchingDrop(e) {
      this.project_matching_file = e.dataTransfer.files[0];
      this.uploadProjectMatchingFile();
    },
    handleAKDDrop(e) {
      this.akd_file = e.dataTransfer.files[0];
      this.uploadAKDFile();
    },
    async uploadAKDFile() {
      const apiUrl = getApiBaseUrl();
      if (!this.akd_file) return;
      this.loading = true;

      const formData = new FormData();
      formData.append('file', this.akd_file);
      formData.append('username', this.user);

      axios.post(`${apiUrl}/upload_akd_file/`, formData)
        .then(() => {
            Swal.fire({
              icon: 'success',
              title: 'Erfolg!',
              text: 'AKD-Daten erfolgreich aktualisiert.'
            });
            this.fetchUpdateDates();
          })
          .catch(() => {
            Swal.fire({
              icon: 'error',
              title: 'Fehler!',
              text: 'AKD-Datei-Upload fehlgeschlagen.'
            });
          })  
        .finally(() => this.loading = false);
    },
    selectDigitalProjectMatchingFile() {
    this.$refs.digital_project_matching_fileInput.click();
    },
    handleDigitalProjectMatchingFileSelect(event) {
      this.digital_project_matching_file = event.target.files[0];
      this.uploadDigitalProjectMatchingFile();
    },
    handleDigitalProjectMatchingDrop(e) {
      this.digital_project_matching_file = e.dataTransfer.files[0];
      this.uploadDigitalProjectMatchingFile();
    },
    uploadDigitalProjectMatchingFile() {
  const apiUrl = getApiBaseUrl();
  if (!this.digital_project_matching_file) return;
  this.loading = true;
  const formData = new FormData();
  formData.append('file', this.digital_project_matching_file);
  formData.append('username', this.user);

  // CSRF-Token holen und Header setzen
  const csrfToken = this.getCsrfToken();
  const config = {
    withCredentials: true
  };
  if (csrfToken) {
    config.headers = {
      'X-CSRFToken': csrfToken
    };
  }

  axios.post(`${apiUrl}/upload_digital_project_matching_file/`, formData, config)
    .then(() => {
      Swal.fire({
        icon: 'success',
        title: 'Erfolg!',
        text: 'Digital-Projekt-Matching-Daten erfolgreich aktualisiert.'
      });
      this.fetchUpdateDates();
    })
    .catch(() => {
      Swal.fire({
        icon: 'error',
        title: 'Fehler!',
        text: 'Digital-Projekt-Matching-Datei-Upload fehlgeschlagen.'
      });
    })
    .finally(() => {
      this.loading = false;
    });
    },
    uploadProjectMatchingFile() {
      const apiUrl = getApiBaseUrl();
      if (!this.project_matching_file) return;
      this.loading = true;
      const formData = new FormData();
      formData.append('file', this.project_matching_file);
      formData.append('username', this.user);

      axios.post(`${apiUrl}/upload_project_matching_file/`, formData)
        .then(() => {
          Swal.fire({
            icon: 'success',
            title: 'Erfolg!',
            text: 'Projekt-Matching-Daten erfolgreich aktualisiert.'
          });
          this.fetchUpdateDates();
        })
        .catch(() => {
          Swal.fire({
            icon: 'error',
            title: 'Fehler!',
            text: 'Merch-Projekt-Matching-Datei-Upload fehlgeschlagen.'
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    selectErgebnisFile() {
      this.$refs.ergebnis_fileInput.click();
    },
    handleErgebnisFileSelect(event) {
      this.ergebnis_file = event.target.files[0];
      this.uploadErgebnisFile();
    },
    handleErgebnisDrop(e) {
      this.ergebnis_file = e.dataTransfer.files[0];
      this.uploadErgebnisFile();
    },
    uploadErgebnisFile() {
      const apiUrl = getApiBaseUrl();
      if (!this.ergebnis_file) return;
      this.loading = true;
      const formData = new FormData();
      formData.append('file', this.ergebnis_file);
      formData.append('username', this.user);
      axios.post(`${apiUrl}/upload_ergebnis_file/`, formData)
        .then(() => {
          Swal.fire({
            icon: 'success',
            title: 'Erfolg!',
            text: 'Digital Ergebnis-Daten erfolgreich aktualisiert.'
          });
          this.fetchUpdateDates();
        })
        .catch(() => {
          Swal.fire({
            icon: 'error',
            title: 'Fehler!',
            text: 'Digital Ergebnis-Datei-Upload fehlgeschlagen.'
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    selectPlanFile() {
      this.$refs.plan_fileInput.click();
    },
    handlePlanFileSelect(event) {
      this.plan_file = event.target.files[0];
      this.uploadPlanFile();
    },
    handlePlanDrop(e) {
      this.plan_file = e.dataTransfer.files[0];
      this.uploadPlanFile();
    },
    async uploadPlanFile() {
      const apiUrl = getApiBaseUrl();
      if (!this.plan_file) return;
      this.loading = true;
      const formData = new FormData();
      formData.append('file', this.plan_file);
      formData.append('username', this.user);

      axios.post(`${apiUrl}/upload_plan_file/`, formData)
        .then((response) => {
          const msg = response.data && response.data.message ? response.data.message : 'Plan-Daten erfolgreich aktualisiert.';
          const isWarning = msg.includes('0 Datensätze');
          Swal.fire({
            icon: isWarning ? 'warning' : 'success',
            title: isWarning ? 'Hinweis' : 'Erfolg!',
            text: msg
          });
          this.fetchUpdateDates();
        })
        .catch((error) => {
          const errorMessage = error.response && error.response.data && error.response.data.error 
            ? error.response.data.error 
            : 'Plan-Datei-Upload fehlgeschlagen.';
          Swal.fire({
            icon: 'error',
            title: 'Fehler!',
            text: errorMessage
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchClockodoData() {
      this.loading = true;
      const formData = new FormData();
      const apiUrl = getApiBaseUrl();
      formData.append('username', this.user);
      axios.post(`${apiUrl}/fetch_clockodo_data/`, formData)
        .then(() => {
          Swal.fire({
            icon: 'success',
            title: 'Erfolg!',
            text: 'Clockodo-Daten erfolgreich aktualisiert.'
          });
          this.fetchUpdateDates();
        })
        .catch(() => {
          Swal.fire({
            icon: 'error',
            title: 'Fehler!',
            text: 'Clockodo-Daten konnten nicht aktualisiert werden.'
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchUpdateDates() {
      this.loading = true;
      const apiUrl = getApiBaseUrl();
      axios.get(`${apiUrl}/get_update_dates/`)
        .then(response => {
          this.aktu_akd_date = this.adjustTimeForGermany(response.data.aktu_akd_date);
          this.akd_file_name = response.data.akd_file_name || null;
          this.plan_file_name = response.data.plan_file_name || null;
          this.aktu_plan_date = this.adjustTimeForGermany(response.data.aktu_plan_date);
          this.updated_by_plan = response.data.updated_by_plan || '';
          this.ergebnis_file_name = response.data.ergebnis_file_name || null;
          this.project_matching_file_name = response.data.project_matching_file_name || null;
          this.updated_by_akd = response.data.updated_by_akd || '';
          this.updated_by_ergebnis = response.data.updated_by_ergebnis || '';
          this.updated_by_project_matching = response.data.updated_by_project_matching || '';
          this.updated_by_clockodo = response.data.updated_by_clockodo || '';
          this.aktu_ergebnis_date = this.adjustTimeForGermany(response.data.aktu_ergebnis_date);
          this.aktu_clockodo_date = this.adjustTimeForGermany(response.data.aktu_clockodo_date);
          this.aktu_project_matching_date = this.adjustTimeForGermany(response.data.aktu_project_matching_date);
          this.path_akd = response.data.akd_file_path || '';
          this.path_ergebnis = response.data.ergebnis_file_path || '';
          this.path_project_matching = response.data.project_matching_file_path || '';
          this.aktu_digital_project_matching_date = this.adjustTimeForGermany(response.data.digital_matching_date);
          this.digital_project_matching_file_name = response.data.digital_matching_file_name || null;
          this.updated_by_digital_project_matching = response.data.digital_matching_user || '';
          this.digital_project_matching_file = response.data.digital_project_matching_file || null;
          this.user_role = response.data.user_role || '';
        

        })
        .catch(error => {
          alert('Fehler beim Abrufen der Aktualisierungsdaten: ' + error.message);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    fetchFileUploadData() {
      this.loading = true;
      const apiUrl = getApiBaseUrl();
      axios.get(`${apiUrl}/file_uploads/`)
        .then(response => {
          this.akd_file_name = response.data.akd_file_name || null;
          this.plan_file_name = response.data.plan_file_name || null;
          this.ergebnis_file_name = response.data.ergebnis_file_name || null;
          this.project_matching_file_name = response.data.project_matching_file_name || null;
          this.aktu_akd_date = this.adjustTimeForGermany(response.data.aktu_akd_date);
          this.aktu_plan_date = this.adjustTimeForGermany(response.data.aktu_plan_date);
          this.aktu_ergebnis_date = this.adjustTimeForGermany(response.data.aktu_ergebnis_date);
          this.aktu_project_matching_date = this.adjustTimeForGermany(response.data.aktu_project_matching_date);
          this.updated_by_akd = response.data.updated_by_akd || '';
          this.updated_by_plan = response.data.updated_by_plan || '';
          this.updated_by_ergebnis = response.data.updated_by_ergebnis || '';
          this.updated_by_project_matching = response.data.updated_by_project_matching || '';
          this.loading = false;
        })
        .catch(error => {
          alert('Fehler beim Abrufen der Dateidaten: ' + error.message);
        });
    },

  },

};
</script>

<style scoped>
/* Abstand zwischen den Karten */
.row.g-4 {
  --bs-gutter-x: 2rem;
  --bs-gutter-y: 2rem;
}

.col-md-6, .col-12 {
  display: flex;
  flex-direction: column;
}

.flip-card,
.card.drop-area {
  min-height: 340px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
}

.flip-card-inner,
.flip-card-front,
.flip-card-back {
  height: 100%;
}

.flip-card-inner {
  position: relative;
  text-align: center;
  transition: transform 0.6s;
  transform-style: preserve-3d;
  height: 100%;
}

.flip-card-inner.is-flipped {
  transform: rotateY(180deg);
}

.flip-card-front,
.flip-card-back {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  top: 0;
  left: 0;
}

.flip-card-back {
  transform: rotateY(180deg);
}

.drop-area {
  border: 2px dashed var(--color-border);
  padding: 20px;
  cursor: pointer;
  background: var(--color-surface);
  min-height: 340px;
  position: relative;
}

.dm-content {
  width: 100%;
}

/* Bootstrap-Bausteine im Jira-Stil (ADS). */
.dm-content .card {
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-radius-large);
  box-shadow: none;
}

.dm-content .btn-primary {
  border: none;
  border-radius: var(--ds-radius-small);
  background: var(--ds-brand);
  font-weight: 500;
}

.dm-content .btn-primary:hover,
.dm-content .btn-primary:focus {
  background: var(--ds-brand-hovered);
}

.dm-content .table th {
  font-size: 12px;
  font-weight: 700;
  color: var(--ds-text-subtle);
}

.dm-content .form-control {
  border-color: var(--ds-border-input);
  border-radius: var(--ds-radius-small);
}

.loader-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.loader {
  width: 120px;
  height: 120px;
}

.notification-box {
  position: fixed;
  top: 20px;
  right: 20px;
  background-color: var(--color-primary-subtle);
  padding: 10px 15px;
  border-radius: 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  z-index: 9999;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--color-overlay);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
}

.modal-content {
  position: relative; 
  background: var(--color-surface);
  padding: 20px;
  max-width: 1280px;
  width: 100%;
  border-radius: 5px;
  max-height: 80vh;
  overflow-y: auto;
}
.modal-close-button {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--color-negative);
  border: 2px solid var(--color-surface);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.modal-close-icon {
  color: white;
  font-size: 20px;
  font-weight: bold;
  line-height: 1;
}

.modal-close-button:hover {
  background: var(--color-negative);
}

.info-button {
  position: absolute;
  bottom: 10px;
  right: 10px;
  border: none;
  background: none;
  color: var(--color-primary);
  cursor: pointer;
  padding: 5px;
  font-size: 1.2em;
}

.info-button:hover {
  color: var(--color-primary-dark);
}
.download-button {
  position: absolute;
  bottom: 10px;
  right: 45px;
  color: var(--color-primary);
  font-size: 1.2em;
  cursor: pointer;
  background: none;
  border: none;
  padding: 5px;
}
.download-button:hover {
  color: var(--color-primary-dark);
}
</style>